import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {ResizeObserver | null} */
  #stickyObserver = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.#stickyObserver?.disconnect();
    this.#stickyObserver = null;
  }

  /**
   * CYGNUSS 30/10/2026: esta gaveta usa a rotina unica do carrinho (window.cygGaveta, script de toque cedo em
   * header-actions.liquid): abre como popover quando o navegador tem o recurso, senao com showModal como antes.
   * @returns {any}
   */
  get gavetaLeve() {
    return /** @type {any} */ (window).cygGaveta ?? null;
  }

  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open') && !event?.detail?.data?.deferDrawerOpen) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    // CYGNUSS 30/10/2026: as alturas vem de um ResizeObserver de uma leitura so, no lugar do getBoundingClientRect,
    // que forcava o calculo do layout da pagina logo depois de abrir a gaveta. Mesma conta e mesmo atributo de antes;
    // o observador entrega as alturas depois do layout do proprio quadro, antes da pintura.
    this.#stickyObserver?.disconnect();

    /** @type {Map<Element, number>} */
    const heights = new Map();
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        heights.set(entry.target, entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height);
      }

      const drawerHeight = heights.get(dialog);
      const summaryHeight = heights.get(summary);
      if (drawerHeight === undefined || summaryHeight === undefined) return;

      observer.disconnect();
      if (this.#stickyObserver === observer) this.#stickyObserver = null;

      const ratio = summaryHeight / drawerHeight;
      dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
    });

    this.#stickyObserver = observer;
    observer.observe(dialog);
    observer.observe(summary);
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
